import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, Trash2 } from 'lucide-react';
import { useMutation, useLazyQuery } from '@apollo/client';
import { CandidateAttachment, JobAttachment, AttachmentType } from '../../../interfaces';
import { DELETE_CANDIDATE_ATTACHMENT, DELETE_JOB_ATTACHMENT } from '../../../GraphQL/attachments/mutations';
import { GET_ATTACHMENT_DOWNLOAD_URL } from '../../../GraphQL/attachments/queries';

interface AttachmentItemProps {
  attachment: CandidateAttachment | JobAttachment;
  type: 'candidate' | 'job';
  onDelete?: () => void;
}

const getAttachmentTypeDisplay = (type: AttachmentType): string => {
  switch (type) {
    case AttachmentType.Resume:
      return 'Resume';
    case AttachmentType.CoverLetter:
      return 'Cover Letter';
    case AttachmentType.Portfolio:
      return 'Portfolio';
    case AttachmentType.Certificate:
      return 'Certificate';
    case AttachmentType.Reference:
      return 'Reference';
    case AttachmentType.JobDescription:
      return 'Job Description';
    case AttachmentType.Contract:
      return 'Contract';
    case AttachmentType.CompanyDocument:
      return 'Company Document';
    case AttachmentType.JobSpecification:
      return 'Job Specification';
    case AttachmentType.PolicyDocument:
      return 'Policy Document';
    case AttachmentType.Other:
      return 'Other';
    default:
      return 'Unknown';
  }
};

const getAttachmentTypeBadgeVariant = (type: AttachmentType): string => {
  switch (type) {
    case AttachmentType.Resume:
    case AttachmentType.JobDescription:
      return 'primary';
    case AttachmentType.CoverLetter:
    case AttachmentType.Contract:
      return 'secondary';
    case AttachmentType.Portfolio:
    case AttachmentType.CompanyDocument:
      return 'info';
    case AttachmentType.Certificate:
    case AttachmentType.JobSpecification:
      return 'success';
    case AttachmentType.Reference:
    case AttachmentType.PolicyDocument:
      return 'warning';
    case AttachmentType.Other:
      return 'secondary';
    default:
      return 'light';
  }
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const AttachmentItem: React.FC<AttachmentItemProps> = ({ attachment, type, onDelete }) => {
  const [getDownloadUrl] = useLazyQuery(GET_ATTACHMENT_DOWNLOAD_URL);
  const [deleteCandidateAttachment] = useMutation(DELETE_CANDIDATE_ATTACHMENT);
  const [deleteJobAttachment] = useMutation(DELETE_JOB_ATTACHMENT);

  const handleDownload = async () => {
    try {
      const { data } = await getDownloadUrl({
        variables: { attachmentId: attachment.id }
      });

      if (data?.attachmentDownloadUrl) {
        window.open(data.attachmentDownloadUrl, '_blank');
      }
    } catch (error) {
      console.error('Error downloading attachment:', error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) {
      return;
    }

    try {
      if (type === 'candidate') {
        await deleteCandidateAttachment({
          variables: { attachmentId: attachment.id }
        });
      } else {
        await deleteJobAttachment({
          variables: { attachmentId: attachment.id }
        });
      }

      onDelete?.();
    } catch (error) {
      console.error('Error deleting attachment:', error);
    }
  };

  return (
    <Card className="mb-2 border-0 bg-light">
      <CardContent className="p-3">
        <div className="d-flex justify-content-between align-items-start">
          <div className="flex-grow-1 me-3">
            <div className="d-flex align-items-center mb-2">
              <h6 className="mb-0 me-2">{attachment.originalFileName}</h6>
              <Badge variant={getAttachmentTypeBadgeVariant(attachment.attachmentType) as any}>
                {getAttachmentTypeDisplay(attachment.attachmentType)}
              </Badge>
            </div>

            {attachment.description && (
              <p className="text-muted small mb-2">{attachment.description}</p>
            )}

            <div className="d-flex align-items-center text-muted small">
              <span className="me-3">{formatFileSize(attachment.fileSizeBytes)}</span>
              <span className="me-3">Uploaded by {attachment.uploadedByName}</span>
              <span>{new Date(attachment.createdAtUtc).toLocaleDateString()}</span>
            </div>
          </div>

          <div className="d-flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              title="Download"
            >
              <Download size={16} />
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDelete}
              title="Delete"
            >
              <Trash2 size={16} />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AttachmentItem;